import { Icon, IconButton, Skeleton, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import React, { FC } from 'react';
import { DeviceType, useBreakpoint } from '../../hooks/useBreakpoint';

export type CfIconButtonProps =
  | {
      className: string;
      color?: 'primary' | 'secondary' | 'black';
      onClick?: (e: HTMLButtonElement) => void;
      dataTestId?: string;
      sx?: SxProps<Theme>;
      loadingPlaceholder?: never;
      badge?: (props: { children: React.ReactNode[] }) => React.ReactNode;
      iconSize?: 'small' | 'medium' | 'large';
      disabled?: boolean;
      id?: string;
      baseClassName?: string;
    }
  | {
      className: string;
      dataTestId?: string;
      iconSize?: 'small' | 'medium' | 'large';
      sx?: SxProps<Theme>;
      loadingPlaceholder: true;
      disabled?: boolean;
    };

/**
 * @param props.className - Name of icon class to use
 * @param props.color - Color the icon should use (primary, secondary)
 * @param props.dataTestId - Test id of the icon button
 * @param props.loadingPlaceholder - Boolean indicating if the loading placeholder should be shown
 * @param props.badge - Badge component to be used in the icon button
 * @param props.iconSize - The size of the icon to be used in the icon button
 * @param props.disabled - Boolean indicating if the button is disabled
 * @param props.id - The id of the icon button
 * @param props.onClick handler that fires on 'enter' key press and mouse click
 * @param props.variant adjusts button size based upon use-case. Augment as new use cases are introduced. Varaints are responsive
 * @param props.sx allows for adding margin to the button. Avoid overriding the default styles (sizing, padding, etc.)
 */
export const CfIconButton: FC<CfIconButtonProps> = (props: CfIconButtonProps) => {
  const deviceType = useBreakpoint().deviceType;
  const iconSize = props.iconSize ? props.iconSize : deviceType === DeviceType.Desktop ? 'small' : 'medium';
  const buttonSize = deviceType === DeviceType.Desktop ? 'small' : 'medium';

  if (props.loadingPlaceholder) {
    return (
      <Skeleton variant='circular'>
        <IconButton
          size={buttonSize}
          sx={{ height: { xs: 40, xl: 32 }, width: { xs: 40, xl: 32 }, ...props.sx }}
          data-testid={props.dataTestId}
          disabled={props.disabled}
        >
          <Icon className={props.className} fontSize={iconSize} />
        </IconButton>
      </Skeleton>
    );
  }

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation?.();
    props.onClick?.(e.currentTarget);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    e?.stopPropagation?.();
    if (e.key.toLowerCase() == 'enter') props.onClick?.(e.currentTarget);
  };

  const icon = (
    <Icon
      key={props.badge ? `cf-badge-icon-${props.className}` : undefined}
      className={props.className}
      fontSize={iconSize}
      baseClassName={props.baseClassName}
      color={props.color === 'black' ? undefined : props.color ?? 'primary'}
    />
  );

  return (
    <IconButton
      id={props.id}
      data-testid={props.dataTestId}
      disabled={props.disabled}
      size={buttonSize}
      sx={{ height: { xs: 40, xl: 32 }, width: { xs: 40, xl: 32 }, ...props.sx }}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {props.badge ? props.badge?.({ children: [icon] }) : icon}
    </IconButton>
  );
};
